import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Landing from "@/pages/landing/Landing";
import Customers from "@/pages/sales/Customers";
import Invoices from "@/pages/sales/Invoices";
import InvoiceDetail from "@/pages/sales/InvoiceDetail";
import InvoiceForm from "@/pages/sales/InvoiceForm";
import Estimates from "@/pages/sales/Estimates";
import DeliveryChallans from "@/pages/sales/DeliveryChallans";
import RecurringInvoices from "@/pages/sales/RecurringInvoices";
import CreditNotes from "@/pages/sales/CreditNotes";
import PaymentsReceived from "@/pages/sales/PaymentsReceived";
import Vendors from "@/pages/purchases/Vendors";
import PurchaseOrders from "@/pages/purchases/PurchaseOrders";
import Bills from "@/pages/purchases/Bills";
import RecurringBills from "@/pages/purchases/RecurringBills";
import VendorCredits from "@/pages/purchases/VendorCredits";
import PaymentsMade from "@/pages/purchases/PaymentsMade";
import BankAccounts from "@/pages/banking/BankAccounts";
import Reconciliation from "@/pages/banking/Reconciliation";
import Transfers from "@/pages/banking/Transfers";
import BankRules from "@/pages/banking/BankRules";
import ChartOfAccounts from "@/pages/accounting/ChartOfAccounts";
import ManualJournals from "@/pages/accounting/ManualJournals";
import GeneralLedger from "@/pages/accounting/GeneralLedger";
import TrialBalance from "@/pages/accounting/TrialBalance";
import DayBook from "@/pages/accounting/DayBook";
import FiscalPeriods from "@/pages/accounting/FiscalPeriods";
import MultiCurrency from "@/pages/accounting/MultiCurrency";
import GSTSettings from "@/pages/taxes/GSTSettings";
import GSTReturns from "@/pages/taxes/GSTReturns";
import EInvoicing from "@/pages/taxes/EInvoicing";
import EWayBills from "@/pages/taxes/EWayBills";
import Items from "@/pages/inventory/Items";
import Warehouses from "@/pages/inventory/Warehouses";
import StockAdjustments from "@/pages/inventory/StockAdjustments";
import StockTransfers from "@/pages/inventory/StockTransfers";
import FixedAssets from "@/pages/inventory/FixedAssets";
import Projects from "@/pages/projects/Projects";
import Timesheets from "@/pages/projects/Timesheets";
import ReportsHub from "@/pages/reports/ReportsHub";
import ProfitLoss from "@/pages/reports/ProfitLoss";
import BalanceSheet from "@/pages/reports/BalanceSheet";
import CashFlow from "@/pages/reports/CashFlow";
import ARAging from "@/pages/reports/ARAging";
import APAging from "@/pages/reports/APAging";
import GSTSummary from "@/pages/reports/GSTSummary";
import SalesByCustomer from "@/pages/reports/SalesByCustomer";
import PurchasesByVendor from "@/pages/reports/PurchasesByVendor";
import InventoryValuation from "@/pages/reports/InventoryValuation";
import AuditLog from "@/pages/reports/AuditLog";
import AICopilot from "@/pages/AICopilot";
import Settings from "@/pages/Settings";
import InvoiceTemplates from "@/pages/settings/InvoiceTemplates";
import ZohoImport from "@/pages/settings/ZohoImport";
import AdminMetrics from "@/pages/settings/AdminMetrics";
import PdfViewer from "@/pages/PdfViewer";
import Budgets from "@/pages/reports/Budgets";
import Party360 from "@/pages/Party360";
import TDS from "@/pages/taxes/TDS";
import GSTRecon from "@/pages/taxes/GSTRecon";
import CashForecast from "@/pages/reports/CashForecast";
import About from "@/pages/public/About";
import Changelog from "@/pages/public/Changelog";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Security from "@/pages/public/Security";

function ProtectedRoute() {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
    if (!user) {
        if (location.pathname === "/") return <Landing />;
        return <Navigate to="/login" replace />;
    }
    return <Outlet />;
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/legal/privacy" element={<Privacy />} />
                    <Route path="/legal/terms" element={<Terms />} />
                    <Route path="/legal/security" element={<Security />} />
                    <Route element={<ProtectedRoute />}>
                        <Route element={<AppShell />}>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/sales/customers" element={<Customers />} />
                            <Route path="/sales/invoices" element={<Invoices />} />
                            <Route path="/sales/invoices/new" element={<InvoiceForm />} />
                            <Route path="/sales/invoices/:id" element={<InvoiceDetail />} />
                            <Route path="/sales/invoices/:id/edit" element={<InvoiceForm />} />
                            <Route path="/sales/estimates" element={<Estimates />} />
                            <Route path="/sales/delivery-challans" element={<DeliveryChallans />} />
                            <Route path="/sales/recurring" element={<RecurringInvoices />} />
                            <Route path="/sales/credit-notes" element={<CreditNotes />} />
                            <Route path="/sales/payments-received" element={<PaymentsReceived />} />
                            <Route path="/purchases/vendors" element={<Vendors />} />
                            <Route path="/purchases/orders" element={<PurchaseOrders />} />
                            <Route path="/purchases/bills" element={<Bills />} />
                            <Route path="/purchases/recurring" element={<RecurringBills />} />
                            <Route path="/purchases/vendor-credits" element={<VendorCredits />} />
                            <Route path="/purchases/payments-made" element={<PaymentsMade />} />
                            <Route path="/banking/accounts" element={<BankAccounts />} />
                            <Route path="/banking/reconciliation" element={<Reconciliation />} />
                            <Route path="/banking/transfers" element={<Transfers />} />
                            <Route path="/banking/rules" element={<BankRules />} />
                            <Route path="/accounting/coa" element={<ChartOfAccounts />} />
                            <Route path="/accounting/journals" element={<ManualJournals />} />
                            <Route path="/accounting/ledger" element={<GeneralLedger />} />
                            <Route path="/accounting/trial-balance" element={<TrialBalance />} />
                            <Route path="/accounting/day-book" element={<DayBook />} />
                            <Route path="/accounting/multi-currency" element={<MultiCurrency />} />
                            <Route path="/accounting/fiscal-periods" element={<FiscalPeriods />} />
                            <Route path="/taxes/gst-settings" element={<GSTSettings />} />
                            <Route path="/taxes/gst-returns" element={<GSTReturns />} />
                            <Route path="/taxes/e-invoicing" element={<EInvoicing />} />
                            <Route path="/taxes/e-way-bills" element={<EWayBills />} />
                            <Route path="/inventory/items" element={<Items />} />
                            <Route path="/inventory/warehouses" element={<Warehouses />} />
                            <Route path="/inventory/adjustments" element={<StockAdjustments />} />
                            <Route path="/inventory/transfers" element={<StockTransfers />} />
                            <Route path="/inventory/fixed-assets" element={<FixedAssets />} />
                            <Route path="/projects" element={<Projects />} />
                            <Route path="/projects/timesheets" element={<Timesheets />} />
                            <Route path="/reports" element={<ReportsHub />} />
                            <Route path="/reports/profit-loss" element={<ProfitLoss />} />
                            <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
                            <Route path="/reports/cash-flow" element={<CashFlow />} />
                            <Route path="/reports/ar-aging" element={<ARAging />} />
                            <Route path="/reports/ap-aging" element={<APAging />} />
                            <Route path="/reports/gst-summary" element={<GSTSummary />} />
                            <Route path="/reports/sales-by-customer" element={<SalesByCustomer />} />
                            <Route path="/reports/purchases-by-vendor" element={<PurchasesByVendor />} />
                            <Route path="/reports/inventory-valuation" element={<InventoryValuation />} />
                            <Route path="/reports/audit-log" element={<AuditLog />} />
                            <Route path="/reports/budgets" element={<Budgets />} />
                            <Route path="/reports/cash-forecast" element={<CashForecast />} />
                            <Route path="/taxes/tds" element={<TDS />} />
                            <Route path="/taxes/gst-recon" element={<GSTRecon />} />
                            <Route path="/sales/customers/:id" element={<Party360 partyType="customer" />} />
                            <Route path="/purchases/vendors/:id" element={<Party360 partyType="vendor" />} />
                            <Route path="/copilot" element={<AICopilot />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/settings/invoice-templates" element={<InvoiceTemplates />} />
                            <Route path="/settings/zoho-import" element={<ZohoImport />} />
                            <Route path="/settings/admin-metrics" element={<AdminMetrics />} />
                            <Route path="/pdf-viewer" element={<PdfViewer />} />
                        </Route>
                    </Route>
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}
